import practiceImage from "app/assets/practice-image.png";
import { PRACTICE_LEVEL } from "app/assets/practiceLevel";
import ErrorBoundary from "app/error/ErrorBoundary";
import GameDataProvider from "app/game-data/GameDataProvider";
import useGameData from "app/game-data/useGameData";
import useNewGame from "app/game-data/useNewGame";
import GameOver from "app/game-over/GameOver";
import Game from "app/game/Game";
import GameStateProvider from "app/game/GameStateProvider";
import useGameState, {
  GameAction,
  useGameActionDispatch,
} from "app/game/useGameState";
import { GODOT_EVENT, useGodotListener } from "app/godot-embed/godot-bridge";
import HowToPlay from "app/how-to-play/HowToPlay";
import TopNav from "app/top-nav/TopNav";
import Credits from "app/ui/Credits";
import PracticeRoundCompletePanel from "app/ui/PracticeRoundCompletePanel";
import Settings from "app/ui/Settings";
import TitleScreen from "app/ui/TitleScreen";
import cn from "app/util/classnames";
import { useGlobalReset } from "app/util/useResetableStorage";
import { GameConfigProvider } from "game-config";
import mixpanel from "mixpanel-browser";
import { useCallback, useState } from "react";
import VictoryPanel from "./ui/VictoryPanel";
import { useImageDimensions } from "./util/useImageDimensions";

// Different overlays that can appear on top of the loaded Godot canvas.
const GAMEPLAY_OVERLAY = {
  NONE: "none",
  PRACTICE_ROUND_COMPLETE: "practiceRoundComplete",
  HOW_TO_PLAY: "howToPlay",
  SETTINGS: "settings",
  CREDITS: "credits",
  VICTORY_PANEL: "victoryPanel",
} as const;

type GameplayOverlay = (typeof GAMEPLAY_OVERLAY)[keyof typeof GAMEPLAY_OVERLAY];

function App() {
  // Check for new game data
  useNewGame();

  const [isTitleScreenDismissed, setIsTitleScreenDismissed] = useState(false);
  const [isPracticeRound, setIsPracticeRound] = useState(false);

  // True when the user has backed out of the results screen to view their
  // completed version of the level.
  const [isViewingCompletedGrid, setIsViewingCompletedGrid] = useState(false);

  const [gameplayOverlay, setGameplayOverlay] = useState<GameplayOverlay>(
    GAMEPLAY_OVERLAY.NONE,
  );

  const dispatch = useGameActionDispatch();

  const {
    id: game_id,
    isGameOver,
    gameTime,
    isDailyLevelStarted,
    isNewPlayer,
  } = useGameState();

  // Determine what level to use. The practice level is hardcoded, so we also
  // use it as a fallback if game data could not be fetched.
  const gameData = useGameData();
  const levelLayout = JSON.stringify(
    gameData?.level == null || isPracticeRound
      ? PRACTICE_LEVEL
      : gameData?.level.puzzle,
  );

  // Store the dimensions of the TED screenshot image when it loads so that we
  // can pass it to the victory panel at the end of the game.
  const { width: tedImageWidth, height: tedImageHeight } = useImageDimensions(
    gameData?.level?.originalScreenshotUrl ?? "",
  );

  function handleTitleScreenDismiss() {
    setIsTitleScreenDismissed(true);

    // If it's a new player, force them into the HowToPlay screen
    if (isNewPlayer) {
      mixpanel.track("onboarding_start", { game_id });
      setGameplayOverlay(GAMEPLAY_OVERLAY.HOW_TO_PLAY);
    } else {
      if (!isDailyLevelStarted) {
        mixpanel.track("game_start", { game_id });
        dispatch({ type: GameAction.DAILY_LEVEL_START });
      }
    }
  }

  // When How to Play is chosen from the game menu
  function handleHowToPlayClick() {
    setGameplayOverlay(GAMEPLAY_OVERLAY.HOW_TO_PLAY);
    mixpanel.track_pageview({
      game_id,
      page: `How to Play`,
    });
  }

  function handleHowToPlayDismiss(startPracticeLevel: boolean) {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);

    // If the user pressed the 'Practice Level' button on HowToPlay
    if (startPracticeLevel && !isPracticeRound) {
      setIsPracticeRound(true);
      dispatch({ type: GameAction.PRACTICE_ROUND_START });
      mixpanel.track("practice_round_start", { game_id });
      return;
    }

    // Otherwise, just start/resume the normal level
    if (!isDailyLevelStarted) {
      mixpanel.track("game_start", { game_id });
      dispatch({ type: GameAction.DAILY_LEVEL_START });
    }
  }

  // When Practice Level is chosen from the game menu
  function handlePracticeRoundClick() {
    dispatch({ type: GameAction.PRACTICE_ROUND_START });
    mixpanel.track("practice_round_start", { game_id });
    setIsPracticeRound(true);
  }

  function handlePracticeRoundCompletePanelDismiss() {
    setIsPracticeRound(false);
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
    if (!isDailyLevelStarted) {
      mixpanel.track("game_start", { game_id });
      dispatch({ type: GameAction.DAILY_LEVEL_START });
    }
  }

  const handleGameWin = useCallback(() => {
    // If it was a practice round, just turn on the practice round complete
    // overlay.
    if (isPracticeRound) {
      setGameplayOverlay(GAMEPLAY_OVERLAY.PRACTICE_ROUND_COMPLETE);
      return;
    }

    if (!isGameOver) {
      mixpanel.track("game_completed", { game_id, play_time: gameTime });
      setGameplayOverlay(GAMEPLAY_OVERLAY.VICTORY_PANEL);
    }
  }, [isPracticeRound, game_id, gameTime, isGameOver]);

  useGodotListener(GODOT_EVENT.VICTORY, handleGameWin);

  const onVictoryPanelDismiss = useCallback(() => {
    setGameplayOverlay(GAMEPLAY_OVERLAY.NONE);
    dispatch({ type: GameAction.GAME_OVER });
  }, [dispatch]);

  if (!isViewingCompletedGrid) {
    if (isGameOver) {
      return (
        <GameOver onViewCompletedGrid={() => setIsViewingCompletedGrid(true)} />
      );
    }

    if (!isTitleScreenDismissed) {
      return <TitleScreen onDismiss={handleTitleScreenDismiss} />;
    }
  }

  // Gameplay screen
  return (
    <div
      className={cn(
        "w-full h-dvh min-h-0",
        "flex flex-col overflow-auto",
        "relative",
        "overflow-clip",
      )}
    >
      <TopNav
        hideMenuButton={false}
        onHowToPlayClick={handleHowToPlayClick}
        onSettingsClick={() => setGameplayOverlay(GAMEPLAY_OVERLAY.SETTINGS)}
        onCreditsClick={() => setGameplayOverlay(GAMEPLAY_OVERLAY.CREDITS)}
        showPracticeRoundOption={!isViewingCompletedGrid && !isPracticeRound}
        onPracticeRoundClick={handlePracticeRoundClick}
        showGameTimer={
          gameplayOverlay === GAMEPLAY_OVERLAY.NONE &&
          !isPracticeRound &&
          !isViewingCompletedGrid
        }
      />
      <Game
        levelLayout={levelLayout}
        victoryImageUrl={
          isPracticeRound
            ? practiceImage
            : (gameData?.level.originalScreenshotUrl ?? "")
        }
        saveBoardState={!isPracticeRound}
        isAlreadyComplete={isViewingCompletedGrid}
        onBackButtonClick={() => setIsViewingCompletedGrid(false)}
      />
      {gameplayOverlay !== GAMEPLAY_OVERLAY.NONE && (
        <div // Overlay container
          className="absolute inset-0 z-20"
        >
          {gameplayOverlay === GAMEPLAY_OVERLAY.PRACTICE_ROUND_COMPLETE && (
            <PracticeRoundCompletePanel
              onDismiss={handlePracticeRoundCompletePanelDismiss}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.HOW_TO_PLAY && (
            <HowToPlay
              showPracticeButton={!isPracticeRound && !isViewingCompletedGrid}
              onDismiss={handleHowToPlayDismiss}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.SETTINGS && (
            <Settings
              onClose={() => setGameplayOverlay(GAMEPLAY_OVERLAY.NONE)}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.CREDITS && (
            <Credits
              onClose={() => setGameplayOverlay(GAMEPLAY_OVERLAY.NONE)}
            />
          )}
          {gameplayOverlay === GAMEPLAY_OVERLAY.VICTORY_PANEL && (
            <VictoryPanel
              tedImageName={gameData?.level?.name ?? ""}
              tedImageUrl={gameData?.level?.originalScreenshotUrl ?? ""}
              tedImageWidth={tedImageWidth}
              tedImageHeight={tedImageHeight}
              solvedBoardLayout={gameData?.level?.puzzle ?? ""}
              onDismiss={onVictoryPanelDismiss}
            />
          )}
        </div>
      )}
    </div>
  );
}

function WrappedApp() {
  useGlobalReset();

  return (
    <GameConfigProvider>
      <ErrorBoundary>
        <GameDataProvider>
          <GameStateProvider>
            <App />
          </GameStateProvider>
        </GameDataProvider>
      </ErrorBoundary>
    </GameConfigProvider>
  );
}

export default WrappedApp;
